/**
 * Orders Service
 * Manages order operations in Firestore
 * Orders are stored at: ClientsV2/{client_makat}/Orders/{order_uid}
 */
import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore'
import { getFirebaseDb } from '../firebase/firebase.js'

/**
 * Order status enum
 */
export const ORDER_STATUS = {
  NEW: 'new', // Pre-sale order created (no payment expected)
  PENDING: 'pending', // Order created, not paid
  PAID: 'paid', // Order paid
  PRIORITY: 'priority', // Order pushed to Priority ERP
  PROCESSING: 'processing', // Order being processed
  SHIPPED: 'shipped', // Order shipped
  DELIVERED: 'delivered', // Order delivered
  CANCELLED: 'cancelled', // Order cancelled
}

/**
 * Get the next order number using a transaction
 * Increments Counters/orderCounter.orderNum
 * @returns {Promise<number>} The next order number
 */
export async function getNextOrderNumber() {
  const db = getFirebaseDb()
  const counterRef = doc(db, 'Counters', 'orderCounter')

  const newOrderNum = await runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef)

    let currentNum = 0
    if (counterDoc.exists()) {
      currentNum = counterDoc.data().orderNum || 0
    }

    const nextNum = currentNum + 1
    transaction.update(counterRef, { orderNum: nextNum })

    return nextNum
  })

  return newOrderNum
}

/**
 * Create a new order
 * @param {string} clientMakat - The client's makat code
 * @param {Object} orderData - The order data
 * @param {Object} orderData.client - Client info snapshot
 * @param {Array} orderData.items - Cart items snapshot
 * @param {Object} orderData.totals - Pricing totals snapshot
 * @param {Object} orderData.deposit - Deposit summary snapshot
 * @param {string} orderData.userUid - The user who placed the order
 * @param {string} orderData.userEmail - The user's email
 * @returns {Promise<{orderId: string, orderNumber: number}>}
 */
export async function createOrder(clientMakat, orderData) {
  if (!clientMakat) {
    throw new Error('Client makat is required to create an order')
  }

  const db = getFirebaseDb()

  // Get next order number
  const orderNumber = await getNextOrderNumber()

  const isPreSale = orderData.pre_sale === true

  // Build order document
  const order = {
    // Order identification
    orderNumber,
    status: isPreSale ? ORDER_STATUS.NEW : ORDER_STATUS.PENDING,
    source: 'b2b', // Identifies this as a ClientsV2 order for collectionGroup queries
    pre_sale: isPreSale,

    // Client info (snapshot at order time)
    client: {
      makat: clientMakat,
      name: orderData.client?.name || '',
      address: orderData.client?.address || '',
      city: orderData.client?.city || '',
      phone: orderData.client?.phone || '',
      email: orderData.client?.email || '',
      firstOrder: orderData.firstOrder || false,
    },

    // User info
    createdBy: {
      uid: orderData.userUid || '',
      email: orderData.userEmail || '',
    },

    // Items snapshot (full product details at order time)
    items: orderData.items.map((item) => ({
      makat: item.makat,
      barcode: item.barcode || null,
      boxSize: item.boxSize || null,
      name: item.name,
      qty: item.qty,
      // Price snapshot at order time
      unitPricePreVatPreDeposit: item.unitPricePreVatPreDeposit,
      catalogPrice: item.catalogPrice,
      lineTotalPreVat: item.unitPricePreVatPreDeposit * item.qty,
      // Promo/discount info
      isPromoLine: item.isPromoLine || false,
      isDiscountLine: item.isDiscountLine || false,
      promo: item.promo
        ? {
            id: item.promo.id,
            label: item.promo.label,
            description: item.promo.description,
          }
        : null,
      discount: item.discount
        ? {
            paritMerakez: item.discount.paritMerakez,
            discountPercent: item.discount.discountPercent,
            savings: item.discount.savings,
          }
        : null,
      // Product info for cart restoration
      image: item.image || null,
    })),

    // Totals snapshot
    totals: {
      itemsSubtotalPreVat: orderData.totals.itemsSubtotalPreVat,
      depositPreVat: orderData.totals.depositPreVat || 0,
      totalPreVat: orderData.totals.totalPreVat,
      vatAmount: orderData.totals.vatAmount,
      totalWithVat: orderData.totals.totalWithVat,
      promoSavingsPreVat: orderData.totals.promoSavingsPreVat || 0,
      discountSavingsPreVat: orderData.totals.discountSavingsPreVat || 0,
      lineCount: orderData.totals.lineCount || orderData.items.length,
    },

    // Deposit info
    deposit: {
      depositPreVat: orderData.deposit?.depositPreVat || 0,
      depositUnits: orderData.deposit?.depositUnits || 0,
      hasDeposit: orderData.deposit?.hasDeposit || false,
    },

    // Metadata
    itemCount: orderData.items.reduce((sum, item) => sum + item.qty, 0),
    uniqueItemCount: orderData.items.length,

    // Points to award after payment
    pointsToAward: orderData.pointsToAward || 0,
    pointsAwarded: false,

    // Timestamps
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    paidAt: null,
  }

  // Save to ClientsV2/{clientMakat}/Orders subcollection
  const ordersRef = collection(db, 'ClientsV2', clientMakat, 'Orders')
  const docRef = await addDoc(ordersRef, order)

  // Note: first_order flag is now flipped server-side in the PayPlus callback
  // after successful payment, not at order creation time

  return {
    orderId: docRef.id,
    orderNumber,
  }
}

/**
 * Get all orders for a client
 * @param {string} clientMakat - The client's makat code
 * @returns {Promise<Array>} Array of orders sorted by creation date (newest first)
 */
export async function getClientOrders(clientMakat) {
  if (!clientMakat) {
    return []
  }

  const db = getFirebaseDb()
  const ordersRef = collection(db, 'ClientsV2', clientMakat, 'Orders')
  const q = query(ordersRef, orderBy('createdAt', 'desc'))

  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }))
}

/**
 * Get all orders across all clients (Admin use)
 * Uses collectionGroup query to fetch all Orders subcollections
 * @returns {Promise<Array>} Array of all orders sorted by creation date (newest first)
 */
export async function getAllOrders() {
  const db = getFirebaseDb()

  // Use collectionGroup to query all 'Orders' subcollections
  // Filter by source='b2b' to only get ClientsV2 orders (not other Orders collections)
  const ordersGroup = collectionGroup(db, 'Orders')
  const q = query(ordersGroup, where('source', '==', 'b2b'), orderBy('createdAt', 'desc'))

  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => {
    // Extract clientMakat from the document path: ClientsV2/{clientMakat}/Orders/{orderId}
    const pathSegments = doc.ref.path.split('/')
    const clientMakat = pathSegments[1] // Index 1 is the clientMakat

    return {
      id: doc.id,
      clientMakat,
      ...doc.data(),
    }
  })
}

/**
 * Fetch orders with server-side filters and cursor-based pagination.
 * Uses collectionGroup('Orders') with composite filters.
 *
 * @param {Object} options
 * @param {string}  [options.status]    - Filter by order status (equality)
 * @param {string[]} [options.cities]   - Filter by client.city (in array, max 30)
 * @param {Date}    [options.dateFrom]  - Filter createdAt >= (range)
 * @param {Date}    [options.dateTo]    - Filter createdAt <= (range)
 * @param {number}  [options.pageSize=20] - Number of docs per page
 * @param {import('firebase/firestore').DocumentSnapshot} [options.lastDoc] - Cursor for next page
 * @returns {Promise<{ orders: Array, lastDoc: DocumentSnapshot|null, hasMore: boolean }>}
 */
export async function getOrdersPaginated({
  status = '',
  cities = [],
  dateFrom = null,
  dateTo = null,
  pageSize = 20,
  lastDoc = null,
} = {}) {
  const db = getFirebaseDb()
  const ordersGroup = collectionGroup(db, 'Orders')

  const constraints = [where('source', '==', 'b2b')]

  if (status) {
    constraints.push(where('status', '==', status))
  }
  if (cities.length > 0) {
    constraints.push(where('client.city', 'in', cities))
  }
  if (dateFrom) {
    constraints.push(where('createdAt', '>=', Timestamp.fromDate(dateFrom)))
  }
  if (dateTo) {
    constraints.push(where('createdAt', '<=', Timestamp.fromDate(dateTo)))
  }

  constraints.push(orderBy('createdAt', 'desc'))

  if (lastDoc) {
    constraints.push(startAfter(lastDoc))
  }

  constraints.push(limit(pageSize + 1))

  const q = query(ordersGroup, ...constraints)
  const snapshot = await getDocs(q)

  const hasMore = snapshot.docs.length > pageSize
  const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs

  const orders = docs.map((docSnap) => {
    const pathSegments = docSnap.ref.path.split('/')
    const clientMakat = pathSegments[1]
    return {
      id: docSnap.id,
      clientMakat,
      ...docSnap.data(),
    }
  })

  return {
    orders,
    lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore,
  }
}

/**
 * Get a single order by ID
 * @param {string} clientMakat - The client's makat code
 * @param {string} orderId - The order document ID
 * @returns {Promise<Object|null>} The order or null if not found
 */
export async function getOrder(clientMakat, orderId) {
  if (!clientMakat || !orderId) {
    return null
  }

  const db = getFirebaseDb()
  const orderRef = doc(db, 'ClientsV2', clientMakat, 'Orders', orderId)
  const orderDoc = await getDoc(orderRef)

  if (orderDoc.exists()) {
    return {
      id: orderDoc.id,
      ...orderDoc.data(),
    }
  }

  return null
}

/**
 * Update order status
 * @param {string} clientMakat - The client's makat code
 * @param {string} orderId - The order document ID
 * @param {string} newStatus - The new status (from ORDER_STATUS enum)
 * @returns {Promise<void>}
 */
export async function updateOrderStatus(clientMakat, orderId, newStatus) {
  if (!clientMakat || !orderId) {
    throw new Error('Client makat and order ID are required')
  }

  const db = getFirebaseDb()
  const orderRef = doc(db, 'ClientsV2', clientMakat, 'Orders', orderId)

  const updateData = {
    status: newStatus,
    updatedAt: serverTimestamp(),
  }

  // Set paidAt timestamp if status is paid
  if (newStatus === ORDER_STATUS.PAID) {
    updateData.paidAt = serverTimestamp()
  }

  await updateDoc(orderRef, updateData)
}

/**
 * Delete an order (only allowed for pending orders)
 * @param {string} clientMakat - The client's makat code
 * @param {string} orderId - The order document ID
 * @returns {Promise<void>}
 */
export async function deleteOrder(clientMakat, orderId) {
  if (!clientMakat || !orderId) {
    throw new Error('Client makat and order ID are required')
  }

  const db = getFirebaseDb()
  const orderRef = doc(db, 'ClientsV2', clientMakat, 'Orders', orderId)

  // Verify order exists and is pending before deleting
  const orderDoc = await getDoc(orderRef)
  if (!orderDoc.exists()) {
    throw new Error('Order not found')
  }

  const orderData = orderDoc.data()
  if (orderData.status !== ORDER_STATUS.PENDING) {
    throw new Error('Only pending orders can be deleted')
  }

  await deleteDoc(orderRef)
}

/**
 * Format order number for display (e.g., 123 -> "0000123")
 * @param {number} orderNumber - The order number
 * @returns {string} Formatted order number
 */
export function formatOrderNumber(orderNumber) {
  if (!orderNumber) return ''
  return String(orderNumber).padStart(7, '0')
}

/**
 * Get status display text in Hebrew
 * @param {string} status - Order status
 * @returns {string} Hebrew status text
 */
export function getStatusDisplayText(status) {
  const statusMap = {
    [ORDER_STATUS.NEW]: 'חדש',
    [ORDER_STATUS.PENDING]: 'ממתין לתשלום',
    [ORDER_STATUS.PAID]: 'שולם',
    [ORDER_STATUS.PRIORITY]: 'פריוריטי',
    [ORDER_STATUS.PROCESSING]: 'בטיפול',
    [ORDER_STATUS.SHIPPED]: 'נשלח',
    [ORDER_STATUS.DELIVERED]: 'נמסר',
    [ORDER_STATUS.CANCELLED]: 'בוטל',
  }
  return statusMap[status] || status
}

/**
 * Get status color class
 * @param {string} status - Order status
 * @returns {string} CSS class name
 */
export function getStatusColorClass(status) {
  const colorMap = {
    [ORDER_STATUS.NEW]: 'status-new',
    [ORDER_STATUS.PENDING]: 'status-pending',
    [ORDER_STATUS.PAID]: 'status-paid',
    [ORDER_STATUS.PRIORITY]: 'status-priority',
    [ORDER_STATUS.PROCESSING]: 'status-processing',
    [ORDER_STATUS.SHIPPED]: 'status-shipped',
    [ORDER_STATUS.DELIVERED]: 'status-delivered',
    [ORDER_STATUS.CANCELLED]: 'status-cancelled',
  }
  return colorMap[status] || 'status-pending'
}
